import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { setPrecinctLabel } from '@store/actionSlices/appConfig'
import {
  InteractivePlanType,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import Container from '@components/container'
import DataHandler from '@components/data-handler'
import FILTER_INITIAL_STATE_HOUSE_AND_LAND from '@components/filter/filterStateHouseAndLand'
import PrecinctCard from '@components/precinct-card'
import ProjectSwitchToggle from '@components/project-switch/ProjectSwitchToggle'
import useProjectSwitch from '@components/project-switch/useProjectSwitch'

import {
  PrecinctListItemInterface,
  selectPrecinctListFromResult,
  useGetPrecinctListQuery,
} from '@api/houseAndLand'
import {
  Polygon,
  selectFromResult as selectInteractiveFromResult,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import AreaSkeleton from './area-skeleton'
import PrecinctList from './precinct-list'

export interface AreaViewHouseAndLandProps {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  pageTitle: string
}

function AreaViewHouseAndLand({
  projectIdentity,
  session,
  pageTitle,
}: AreaViewHouseAndLandProps) {
  const dispatch = useDispatch()
  const location = useLocation()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const [areaViews, setAreaViews] = useState<Polygon[]>([])
  const [precinctList, setPrecinctList] = useState<PrecinctListItemInterface[]>(
    []
  )
  const [isAreaDataReady, setIsAreaDataReady] = useState<boolean>(false)

  const interactiveQueryResult = useGetInteractivePlanQuery(
    {
      projectName: projectIdentity.projectId,
      type: InteractivePlanType.AreaView,
    },
    { selectFromResult: selectInteractiveFromResult }
  )

  const interactivePrecinctQueryResult = useGetInteractivePlanQuery(
    {
      projectName: projectIdentity.projectId,
      type: InteractivePlanType.Precinct,
    },
    { selectFromResult: selectInteractiveFromResult }
  )

  const precinctsQueryResult = useGetPrecinctListQuery(
    { projectName: projectIdentity.projectId },
    {
      selectFromResult: selectPrecinctListFromResult,
    }
  )

  const updateTriggerBuilding = (state: boolean) =>
    firebaseControlQuery.update({
      [`areaView.triggerBuilding`]: state,
    })

  const handleMapClick = async (groupId: string, label?: string) => {
    await firebaseControlQuery.update({
      [`houseAndLand.activePrecinctId`]: groupId,
    })

    await updateTriggerBuilding(true)
    dispatch(setPrecinctLabel(label || groupId))
  }

  const resetHouseAndLandMap = () =>
    firebaseControlQuery.update({
      [`houseAndLand.activePrecinctId`]: '',
      [`houseAndLand.activeStageId`]: '',
      [`houseAndLand.activeLotId`]: '',
    })

  const resetHouseAndLandView = () =>
    firebaseControlQuery.update({
      [`building.sidePanelFolded`]: true,
      [`houseAndLand.lotFilter`]: FILTER_INITIAL_STATE_HOUSE_AND_LAND,
    })

  const redirectToPrecinct = async () => {
    const { maps } = interactivePrecinctQueryResult
    const redirectToId = Object.keys(maps?.precinct || {})?.[0] || ''

    dispatch(setPrecinctLabel(redirectToId))
    await firebaseControlQuery.update({
      [`houseAndLand.activePrecinctId`]: redirectToId,
    })

    await firebaseControlQuery.updateRoute('precinct')
  }

  const shouldRedirectToPrecinct = useMemo(() => {
    const { maps, isLoaded } = interactiveQueryResult
    const { maps: interactivePrecinctMaps, isLoaded: isPrecinctLoaded } =
      interactivePrecinctQueryResult

    return (
      isLoaded &&
      isPrecinctLoaded &&
      !maps?.areaView?.image &&
      Object.keys(interactivePrecinctMaps?.precinct || {}).length > 0
    )
  }, [interactiveQueryResult.isLoaded, interactivePrecinctQueryResult.isLoaded])

  useLayoutEffect(() => {
    if (shouldRedirectToPrecinct) {
      redirectToPrecinct()
    }
  }, [shouldRedirectToPrecinct, location])

  useEffect(() => {
    const { maps, isLoaded, isError } = interactiveQueryResult
    const { precinctList: precincts, isLoaded: isPrecinctListLoaded } =
      precinctsQueryResult

    const isLoadedSuccessfully = isLoaded && !isError
    const hasMaps = Object.keys(maps).length > 0
    const isAreaViewsEmpty = areaViews.length === 0
    const hasValidPolygons = maps.areaView?.polygons?.length > 0

    if (isPrecinctListLoaded) {
      setPrecinctList(precincts)
    }

    if (
      isLoadedSuccessfully &&
      hasMaps &&
      isAreaViewsEmpty &&
      hasValidPolygons &&
      isPrecinctListLoaded
    ) {
      setAreaViews(
        maps.areaView.polygons
          .map((res: Polygon) => res)
          ?.sort((a, b) => {
            const aIndex = precinctList.findIndex(
              (item) => item.label === a.groupId
            )
            const bIndex = precinctList.findIndex(
              (item) => item.label === b.groupId
            )
            if (aIndex === -1) {
              return 1
            }
            if (bIndex === -1) {
              return -1
            }
            return aIndex - bIndex
          })
      )
      setIsAreaDataReady(true)
    }
  }, [
    interactiveQueryResult.isLoaded,
    areaViews,
    precinctsQueryResult.isLoaded,
  ])

  useEffect(() => {
    if (!session) {
      return
    }

    const {
      areaView: { triggerBuilding },
      houseAndLand: { activeStageId, activeLotId },
    } = session

    if (activeStageId || activeLotId) {
      resetHouseAndLandMap().catch((err) => console.error(err))
    }

    if (!triggerBuilding) {
      return
    }

    setTimeout(async () => {
      await updateTriggerBuilding(false)
      await resetHouseAndLandView()
      await firebaseControlQuery.updateRoute('precinct')
    }, 1000)
  }, [
    session?.areaView.triggerBuilding,
    session?.houseAndLand.activeStageId,
    session?.houseAndLand.activeLotId,
  ])

  const projectSwitch = useProjectSwitch()

  return (
    <Container>
      <DataHandler
        payloads={[
          {
            name: 'interactiveQueryResult',
            payload: {
              ...interactiveQueryResult,
              data: interactiveQueryResult.maps,
            },
          },
          {
            name: 'interactivePrecinctQueryResult',
            payload: {
              ...interactivePrecinctQueryResult,
              data: interactivePrecinctQueryResult.maps,
            },
          },
          {
            name: 'precinctsQueryResult',
            payload: {
              ...precinctsQueryResult,
              data: precinctsQueryResult.precinctList,
            },
          },
        ]}
        showDefaultNotFound={!shouldRedirectToPrecinct}
        skeletonFrame={<AreaSkeleton />}
      >
        {isAreaDataReady && (
          <div className="h-full w-full px-4">
            <div className="flex h-top-bar w-full items-end text-title font-medium text-neutralColour">
              <div className="pb-5">{pageTitle}</div>
            </div>
            <div className="no-scrollbar h-page-body w-full overflow-auto">
              {projectIdentity.remoteInteractiveBuilding.areaView.useMatrix ? (
                <PrecinctList
                  precinctList={precinctList}
                  handleClick={(argBlock: string) => handleMapClick(argBlock)}
                />
              ) : (
                areaViews.map((res) => (
                  <PrecinctCard
                    key={res.groupId}
                    label={res.label || res.groupId}
                    handleClick={() => handleMapClick(res.groupId, res.label)}
                  />
                ))
              )}
            </div>
          </div>
        )}
        {projectSwitch.enable && (
          <ProjectSwitchToggle
            className="absolute bottom-5 left-5 z-3"
            isPanelVisible={projectSwitch.isPanelVisible}
            isProcessing={projectSwitch.isProcessing}
            projects={projectSwitch.projectGroup}
            handleTogglePanel={projectSwitch.handleTogglePanel}
            handleProjectSelect={projectSwitch.handleProjectSwitch}
            showOverlayOnProcessing
          />
        )}
      </DataHandler>
    </Container>
  )
}

export default connect(
  ({
    projectIdentity,
    firestore: { session },
    whiteLabel,
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    pageTitle:
      mapWhiteLabelsWithDefaults(whiteLabel).remote.areaViewHouseAndLand
        .pageTitle,
  })
)(AreaViewHouseAndLand)
